import type { Json } from "@/types/database";
import { z } from "zod";

export const GAME_ANALYTICS_MESSAGE_TYPE = "playmath.game_event";
export const GAME_ANALYTICS_EVENT_SCHEMA_VERSION = 2;

export const CANONICAL_GAME_EVENT_NAMES = [
  "game_session_started",
  "game_session_paused",
  "game_session_resumed",
  "game_session_ended",
  "level_started",
  "level_completed",
  "level_failed",
  "attempt_restarted",
  "checkpoint_reached",
  "mode_selected",
  "difficulty_selected",
  "button_clicked",
  "answer_submitted",
  "answer_evaluated",
  "hint_used",
  "tutorial_started",
  "tutorial_completed",
  "score_recorded",
  "accuracy_recorded",
  "stars_awarded",
] as const;

export type CanonicalGameEventName = (typeof CANONICAL_GAME_EVENT_NAMES)[number];
export type GameAnalyticsEventName = CanonicalGameEventName | "game_custom_event";
export type GameEventCategory =
  | "session"
  | "progression"
  | "choice"
  | "learning"
  | "result"
  | "custom";
export type GameAnalyticsSurfaceType =
  | "game_page"
  | "topic_page"
  | "embed_page"
  | "preview_page"
  | "unknown";

const jsonValueSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ])
);

const baseKnownGameEventPropertiesSchema = z
  .object({
    session_id: z.string().min(1).optional(),
    attempt_id: z.string().min(1).optional(),
    content_type: z.string().min(1).optional(),
    content_id: z.string().min(1).optional(),
    content_index: z.number().int().nonnegative().optional(),
    level_number: z.number().int().nonnegative().optional(),
    mode_id: z.string().min(1).optional(),
    mode_name: z.string().min(1).optional(),
    difficulty_id: z.string().min(1).optional(),
    difficulty_name: z.string().min(1).optional(),
    button_id: z.string().min(1).optional(),
    button_label: z.string().min(1).optional(),
    screen_name: z.string().min(1).optional(),
    result: z.string().min(1).optional(),
    duration_ms: z.number().int().nonnegative().optional(),
    score: z.number().optional(),
    accuracy: z.number().optional(),
    hint_count: z.number().int().nonnegative().optional(),
    stars_awarded: z.number().int().nonnegative().optional(),
  })
  .catchall(jsonValueSchema);

const baseCustomGameEventPropertiesSchema = baseKnownGameEventPropertiesSchema
  .extend({
    custom_event_name: z.string().min(1),
  })
  .catchall(jsonValueSchema);

function hasContentTarget(properties: {
  content_id?: string;
  content_index?: number;
  level_number?: number;
}): boolean {
  return (
    Boolean(properties.content_id) ||
    properties.content_index !== undefined ||
    properties.level_number !== undefined
  );
}

function requireContentTarget<T extends z.ZodTypeAny>(schema: T, message: string): z.ZodEffects<T> {
  return schema.refine(
    (properties: {
      content_id?: string;
      content_index?: number;
      level_number?: number;
    }) => hasContentTarget(properties),
    { message }
  );
}

function requireOneOf<T extends z.ZodTypeAny>(
  schema: T,
  predicate: (properties: z.infer<T>) => boolean,
  message: string
): z.ZodEffects<T> {
  return schema.refine(predicate, { message });
}

function createGameEventSchema<
  TEventName extends GameAnalyticsEventName,
  TSchema extends z.ZodTypeAny,
>(eventName: TEventName, propertiesSchema: TSchema) {
  return z.object({
    type: z.literal(GAME_ANALYTICS_MESSAGE_TYPE).optional(),
    event_name: z.literal(eventName),
    properties: propertiesSchema,
    occurred_at: z.string().datetime({ offset: true }).optional(),
  });
}

const sessionEventPropertiesSchema = baseKnownGameEventPropertiesSchema;
const sessionEndedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => properties.duration_ms !== undefined,
  "`duration_ms` is required for `game_session_ended`"
);
const progressionEventPropertiesSchema = requireContentTarget(
  baseKnownGameEventPropertiesSchema,
  "A level, content_id, or content_index is required for progression events"
);
const learningTargetPropertiesSchema = requireContentTarget(
  baseKnownGameEventPropertiesSchema,
  "A level, content_id, or content_index is required for learning events"
);
const modeSelectedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => Boolean(properties.mode_id) || Boolean(properties.mode_name),
  "`mode_id` or `mode_name` is required for `mode_selected`"
);
const difficultySelectedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => Boolean(properties.difficulty_id) || Boolean(properties.difficulty_name),
  "`difficulty_id` or `difficulty_name` is required for `difficulty_selected`"
);
const buttonClickedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => Boolean(properties.button_id) || Boolean(properties.button_label),
  "`button_id` or `button_label` is required for `button_clicked`"
);
const scoreRecordedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => properties.score !== undefined,
  "`score` is required for `score_recorded`"
);
const accuracyRecordedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => properties.accuracy !== undefined,
  "`accuracy` is required for `accuracy_recorded`"
);
const starsAwardedPropertiesSchema = requireOneOf(
  baseKnownGameEventPropertiesSchema,
  (properties) => properties.stars_awarded !== undefined,
  "`stars_awarded` is required for `stars_awarded`"
);
const customGameEventPropertiesSchema = requireOneOf(
  baseCustomGameEventPropertiesSchema,
  (properties) => Boolean(properties.custom_event_name),
  "`custom_event_name` is required for `game_custom_event`"
);

const gameEventPayloadSchema = z.union([
  createGameEventSchema("game_session_started", sessionEventPropertiesSchema),
  createGameEventSchema("game_session_paused", sessionEventPropertiesSchema),
  createGameEventSchema("game_session_resumed", sessionEventPropertiesSchema),
  createGameEventSchema("game_session_ended", sessionEndedPropertiesSchema),
  createGameEventSchema("level_started", progressionEventPropertiesSchema),
  createGameEventSchema("level_completed", progressionEventPropertiesSchema),
  createGameEventSchema("level_failed", progressionEventPropertiesSchema),
  createGameEventSchema("attempt_restarted", progressionEventPropertiesSchema),
  createGameEventSchema("checkpoint_reached", progressionEventPropertiesSchema),
  createGameEventSchema("mode_selected", modeSelectedPropertiesSchema),
  createGameEventSchema("difficulty_selected", difficultySelectedPropertiesSchema),
  createGameEventSchema("button_clicked", buttonClickedPropertiesSchema),
  createGameEventSchema("answer_submitted", learningTargetPropertiesSchema),
  createGameEventSchema("answer_evaluated", learningTargetPropertiesSchema),
  createGameEventSchema("hint_used", learningTargetPropertiesSchema),
  createGameEventSchema("tutorial_started", baseKnownGameEventPropertiesSchema),
  createGameEventSchema("tutorial_completed", baseKnownGameEventPropertiesSchema),
  createGameEventSchema("score_recorded", scoreRecordedPropertiesSchema),
  createGameEventSchema("accuracy_recorded", accuracyRecordedPropertiesSchema),
  createGameEventSchema("stars_awarded", starsAwardedPropertiesSchema),
  createGameEventSchema("game_custom_event", customGameEventPropertiesSchema),
]);

export type GameEventPayload = z.infer<typeof gameEventPayloadSchema>;

export type GameEventSource = "web_iframe" | "unity_webgl";

export interface GameAnalyticsContext {
  enabled: boolean;
  game_id: string;
  game_slug: string;
  game_name: string;
  source: GameEventSource;
  asset_id?: string | null;
  version_id?: string | null;
  asset_type?: string | null;
  page_path?: string | null;
  surface_type?: GameAnalyticsSurfaceType;
}

export interface GameAnalyticsRuntimeState {
  session_id: string;
  active_attempt_ids: Map<string, string>;
}

type CaptureFunction = (eventName: string, properties?: Record<string, Json | undefined>) => void;
type GameAnalyticsPayloadParseOptions = {
  requireTypeEnvelope?: boolean;
};

const EVENT_CATEGORY_BY_NAME: Record<GameAnalyticsEventName, GameEventCategory> = {
  game_session_started: "session",
  game_session_paused: "session",
  game_session_resumed: "session",
  game_session_ended: "session",
  level_started: "progression",
  level_completed: "progression",
  level_failed: "progression",
  attempt_restarted: "progression",
  checkpoint_reached: "progression",
  mode_selected: "choice",
  difficulty_selected: "choice",
  button_clicked: "choice",
  answer_submitted: "learning",
  answer_evaluated: "learning",
  hint_used: "learning",
  tutorial_started: "learning",
  tutorial_completed: "learning",
  score_recorded: "result",
  accuracy_recorded: "result",
  stars_awarded: "result",
  game_custom_event: "custom",
};

const ATTEMPT_START_EVENT_NAMES = new Set<GameAnalyticsEventName>([
  "level_started",
  "attempt_restarted",
]);
const ATTEMPT_CORRELATED_EVENT_NAMES = new Set<GameAnalyticsEventName>([
  "level_completed",
  "level_failed",
  "checkpoint_reached",
  "answer_submitted",
  "answer_evaluated",
  "hint_used",
  "score_recorded",
  "accuracy_recorded",
  "stars_awarded",
]);
const LEVEL_LIKE_EVENT_NAMES = new Set<GameAnalyticsEventName>([
  "level_started",
  "level_completed",
  "level_failed",
  "attempt_restarted",
  "checkpoint_reached",
]);
const MAX_ACTIVE_ATTEMPTS = 100;

function createAnalyticsId(prefix: string): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `${prefix}_${globalThis.crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function buildRejectedMessage(reason: string, details?: unknown): void {
  const isProduction =
    process.env.NODE_ENV === "production" || process.env.APP_ENV === "production";

  if (isProduction) {
    return;
  }

  console.warn("Rejected game analytics event:", {
    reason,
    details,
  });
}

export function createGameAnalyticsRuntimeState(
  sessionId: string = createAnalyticsId("session")
): GameAnalyticsRuntimeState {
  return {
    session_id: sessionId,
    active_attempt_ids: new Map<string, string>(),
  };
}

export function getEventCategory(eventName: GameAnalyticsEventName): GameEventCategory {
  return EVENT_CATEGORY_BY_NAME[eventName];
}

export function getSurfaceTypeFromPath(
  pathname: string | null | undefined
): GameAnalyticsSurfaceType {
  if (!pathname) {
    return "unknown";
  }

  if (pathname.startsWith("/embed/")) {
    return "embed_page";
  }

  if (pathname.startsWith("/preview/")) {
    return "preview_page";
  }

  if (pathname.startsWith("/pages/")) {
    return "topic_page";
  }

  if (pathname.startsWith("/games/")) {
    return "game_page";
  }

  return "unknown";
}

function getDerivedContentType(
  eventName: GameAnalyticsEventName,
  properties: Record<string, Json | undefined>
): string | undefined {
  if (typeof properties.content_type === "string" && properties.content_type.length > 0) {
    return properties.content_type;
  }

  if (LEVEL_LIKE_EVENT_NAMES.has(eventName)) {
    return "level";
  }

  if (
    ATTEMPT_CORRELATED_EVENT_NAMES.has(eventName) &&
    (typeof properties.level_number === "number" || typeof properties.content_id === "string")
  ) {
    return "level";
  }

  return undefined;
}

function getContentKey(
  eventName: GameAnalyticsEventName,
  properties: Record<string, Json | undefined>
): string | null {
  const contentIdentity =
    properties.content_id ?? properties.content_index ?? properties.level_number;
  if (contentIdentity === undefined || contentIdentity === null) {
    return null;
  }

  return `${getDerivedContentType(eventName, properties) ?? "content"}:${String(contentIdentity)}`;
}

function normalizeGameEventProperties(
  eventName: GameAnalyticsEventName,
  properties: Record<string, Json | undefined>
): Record<string, Json | undefined> {
  const contentType = getDerivedContentType(eventName, properties);
  return {
    ...properties,
    content_type: contentType ?? properties.content_type,
  };
}

function getIncomingAttemptId(properties: Record<string, Json | undefined>): string | undefined {
  return typeof properties.attempt_id === "string" && properties.attempt_id.length > 0
    ? properties.attempt_id
    : undefined;
}

function evictAttemptIdsIfNeeded(runtimeState: GameAnalyticsRuntimeState): void {
  while (runtimeState.active_attempt_ids.size >= MAX_ACTIVE_ATTEMPTS) {
    const oldestKey = runtimeState.active_attempt_ids.keys().next().value;
    if (!oldestKey) {
      break;
    }
    runtimeState.active_attempt_ids.delete(oldestKey);
  }
}

function rememberAttemptId(
  contentKey: string | null,
  attemptId: string,
  runtimeState: GameAnalyticsRuntimeState
): void {
  if (contentKey) {
    if (!runtimeState.active_attempt_ids.has(contentKey)) {
      evictAttemptIdsIfNeeded(runtimeState);
    }
    runtimeState.active_attempt_ids.set(contentKey, attemptId);
  }
}

function resolveCorrelatedAttemptId(
  contentKey: string | null,
  runtimeState: GameAnalyticsRuntimeState
): string | undefined {
  if (!contentKey) {
    return undefined;
  }

  const existingAttemptId = runtimeState.active_attempt_ids.get(contentKey);
  if (existingAttemptId) {
    return existingAttemptId;
  }

  evictAttemptIdsIfNeeded(runtimeState);
  const generatedAttemptId = createAnalyticsId("attempt");
  runtimeState.active_attempt_ids.set(contentKey, generatedAttemptId);
  return generatedAttemptId;
}

function resolveAttemptId(
  eventName: GameAnalyticsEventName,
  properties: Record<string, Json | undefined>,
  runtimeState: GameAnalyticsRuntimeState
): string | undefined {
  const incomingAttemptId = getIncomingAttemptId(properties);
  const contentKey = getContentKey(eventName, properties);

  if (incomingAttemptId) {
    rememberAttemptId(contentKey, incomingAttemptId, runtimeState);
  }

  if (ATTEMPT_START_EVENT_NAMES.has(eventName)) {
    const attemptId = incomingAttemptId ?? createAnalyticsId("attempt");
    rememberAttemptId(contentKey, attemptId, runtimeState);
    return attemptId;
  }

  if (ATTEMPT_CORRELATED_EVENT_NAMES.has(eventName)) {
    if (incomingAttemptId) {
      return incomingAttemptId;
    }

    return resolveCorrelatedAttemptId(contentKey, runtimeState);
  }

  if (eventName === "game_session_ended") {
    runtimeState.active_attempt_ids.clear();
  }

  return incomingAttemptId;
}

export function parseGameAnalyticsPayload(
  payload: unknown,
  options: GameAnalyticsPayloadParseOptions = {}
): GameEventPayload | null {
  const result = gameEventPayloadSchema.safeParse(payload);
  if (!result.success) {
    buildRejectedMessage("invalid_payload", result.error.flatten());
    return null;
  }

  if (options.requireTypeEnvelope && result.data.type !== GAME_ANALYTICS_MESSAGE_TYPE) {
    buildRejectedMessage("missing_type_envelope", {
      event_name: result.data.event_name,
    });
    return null;
  }

  return result.data;
}

export function captureGameAnalyticsEvent(
  payload: unknown,
  context: GameAnalyticsContext,
  capture: CaptureFunction | undefined,
  runtimeState: GameAnalyticsRuntimeState = createGameAnalyticsRuntimeState(),
  parseOptions: GameAnalyticsPayloadParseOptions = {}
): boolean {
  if (!context.enabled) {
    return false;
  }

  if (!capture) {
    buildRejectedMessage("capture_unavailable");
    return false;
  }

  const parsedPayload = parseGameAnalyticsPayload(payload, parseOptions);
  if (!parsedPayload) {
    return false;
  }

  const normalizedProperties = normalizeGameEventProperties(
    parsedPayload.event_name,
    parsedPayload.properties as Record<string, Json | undefined>
  );
  const attemptId = resolveAttemptId(parsedPayload.event_name, normalizedProperties, runtimeState);
  const surfaceType = context.surface_type ?? getSurfaceTypeFromPath(context.page_path);

  capture(parsedPayload.event_name, {
    ...normalizedProperties,
    session_id: runtimeState.session_id,
    attempt_id: attemptId ?? undefined,
    game_id: context.game_id,
    game_slug: context.game_slug,
    game_name: context.game_name,
    game_event_source: context.source,
    asset_id: context.asset_id ?? undefined,
    version_id: context.version_id ?? undefined,
    asset_type: context.asset_type ?? undefined,
    page_path: context.page_path ?? undefined,
    surface_type: surfaceType,
    event_category: getEventCategory(parsedPayload.event_name),
    event_schema_version: GAME_ANALYTICS_EVENT_SCHEMA_VERSION,
    occurred_at: parsedPayload.occurred_at ?? undefined,
  });

  return true;
}

export function createUnityAnalyticsBridge(
  context: GameAnalyticsContext,
  capture: CaptureFunction | undefined,
  runtimeState: GameAnalyticsRuntimeState = createGameAnalyticsRuntimeState()
): { track: (payload: unknown) => boolean } {
  return {
    track(payload: unknown) {
      return captureGameAnalyticsEvent(payload, context, capture, runtimeState, {
        requireTypeEnvelope: true,
      });
    },
  };
}

export type UnityAnalyticsBridge = ReturnType<typeof createUnityAnalyticsBridge>;

type UnityBridgeRegistry = Map<string, UnityAnalyticsBridge>;

declare global {
  interface Window {
    PlayMathAnalytics?: {
      track: (payload: unknown, bridgeIdOverride?: string) => boolean;
    };
    __playMathUnityAnalyticsBridges?: UnityBridgeRegistry;
  }
}

function getOrCreateUnityBridgeRegistry(): UnityBridgeRegistry {
  if (!window.__playMathUnityAnalyticsBridges) {
    window.__playMathUnityAnalyticsBridges = new Map();
  }

  return window.__playMathUnityAnalyticsBridges;
}

function updateGlobalUnityAnalyticsBridge(registry: UnityBridgeRegistry): void {
  if (registry.size === 0) {
    window.PlayMathAnalytics = undefined;
    return;
  }

  window.PlayMathAnalytics = {
    track(payload: unknown, bridgeIdOverride?: string) {
      const onlyBridgeId = registry.size === 1 ? Array.from(registry.keys())[0] : undefined;
      const targetBridgeId = bridgeIdOverride ?? onlyBridgeId;

      if (!targetBridgeId) {
        buildRejectedMessage("unity_bridge_ambiguous", {
          registered_bridges: Array.from(registry.keys()),
        });
        return false;
      }

      const targetBridge = registry.get(targetBridgeId);
      if (!targetBridge) {
        buildRejectedMessage("unity_bridge_missing", { bridgeId: targetBridgeId });
        return false;
      }

      return targetBridge.track(payload);
    },
  };
}

export function registerUnityAnalyticsBridge(bridgeId: string, bridge: UnityAnalyticsBridge): void {
  const registry = getOrCreateUnityBridgeRegistry();
  if (registry.has(bridgeId)) {
    buildRejectedMessage("unity_bridge_reregistered", { bridgeId });
  }
  registry.set(bridgeId, bridge);
  updateGlobalUnityAnalyticsBridge(registry);
}

export function unregisterUnityAnalyticsBridge(bridgeId: string): void {
  const registry = getOrCreateUnityBridgeRegistry();
  registry.delete(bridgeId);
  updateGlobalUnityAnalyticsBridge(registry);
}

export function reportRejectedAnalyticsEvent(reason: string, details?: unknown): void {
  buildRejectedMessage(reason, details);
}
